import React, { useState } from 'react';
import { Sparkles, ArrowLeft, RotateCcw, Trophy, Gift, User, Phone, Check, X as XIcon } from 'lucide-react';
import { manualDrawApi } from '../../../../services/api';
import { Product } from '../../../../types';

interface Lead {
  id: string;
  customer_name: string;
  whatsapp: string;
  customer_birthdate: string | null;
}

interface DrawExecutionProps {
  selectedProducts: Product[];
  selectedLeads: Lead[];
  onDrawCompleted: (result: any) => void;
  onBack: () => void;
  onReset: () => void;
  drawResult: any | null;
}

const DrawExecution: React.FC<DrawExecutionProps> = ({
  selectedProducts,
  selectedLeads,
  onDrawCompleted,
  onBack,
  onReset,
  drawResult
}) => {
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const executeDraw = async () => {
    try {
      setExecuting(true);
      setError(null);

      const result = await manualDrawApi.executeManualDraw({
        leadIds: selectedLeads.map(l => l.id),
        productIds: selectedProducts.map(p => p.id)
      });

      onDrawCompleted(result);
    } catch (err: any) {
      setError(err.error || 'Erro ao executar sorteio');
      console.error('Erro ao executar sorteio:', err);
    } finally {
      setExecuting(false);
    }
  };

  const formatPhone = (phone: string) => {
    if (!phone) return '—';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // If draw already executed, show result
  if (drawResult) {
    return (
      <div className="space-y-6">
        {/* Success Header */}
        <div className="text-center py-6">
          <div className="mx-auto w-20 h-20 bg-musgo/10 border border-musgo/30 rounded-full flex items-center justify-center mb-4">
            <Trophy className="w-10 h-10 text-musgo" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-black text-tinta leading-tight">
            Sorteio <span className="italic text-bordo">realizado</span>
          </h2>
          <p className="mt-2 text-sm text-tinta-soft italic">
            {drawResult.message}
          </p>
        </div>

        {/* Winner Card */}
        <div className="bg-white border border-tinta/10 shadow-sm rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-6 h-6 text-mostarda-dark" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-tinta">Ganhador(a)</h3>
          </div>

          <div className="text-center py-4 border-y border-tinta/10">
            <p className="font-display text-3xl sm:text-4xl font-black text-bordo italic leading-tight">
              {drawResult.winner.name}
            </p>
            <p className="mt-3 inline-flex items-center gap-2 text-sm text-tinta-soft">
              <Phone className="w-4 h-4" />
              {formatPhone(drawResult.winner.whatsapp)}
            </p>
          </div>
        </div>

        {/* Prizes */}
        <div className="bg-white border border-tinta/10 shadow-sm rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Gift className="w-5 h-5 text-bordo" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-tinta">
              Prêmios ({drawResult.total_prizes})
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {drawResult.prizes.map((prize: any, index: number) => (
              <div key={prize.id} className="bg-creme-light border border-tinta/10 rounded-lg p-3">
                {prize.image_url && (
                  <img
                    src={prize.image_url}
                    alt={prize.name}
                    className="w-full h-24 object-cover rounded-md mb-2 border border-tinta/10"
                  />
                )}
                <p className="font-display text-base font-bold text-tinta leading-tight">
                  <span className="text-bordo italic">{index + 1}.</span> {prize.name}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-white border border-tinta/10 rounded-lg p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft mb-1">Participantes</p>
            <p className="font-display text-3xl font-black text-tinta">
              {drawResult.total_participants}
            </p>
          </div>
          <div className="bg-white border border-tinta/10 rounded-lg p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft mb-1">Prêmios</p>
            <p className="font-display text-3xl font-black text-tinta">
              {drawResult.total_prizes}
            </p>
          </div>
          <div className={`border rounded-lg p-4 ${
            drawResult.notification_sent
              ? 'bg-musgo/10 border-musgo/30'
              : 'bg-creme-light border-tinta/10'
          }`}>
            <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${
              drawResult.notification_sent ? 'text-musgo-dark' : 'text-tinta-soft'
            }`}>WhatsApp Enviado</p>
            <p className={`font-display text-2xl font-black flex items-center gap-2 ${
              drawResult.notification_sent ? 'text-musgo' : 'text-tinta-soft'
            }`}>
              {drawResult.notification_sent ? (
                <>
                  <Check className="w-6 h-6" />
                  Sim
                </>
              ) : (
                <>
                  <XIcon className="w-6 h-6" />
                  Não
                </>
              )}
            </p>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-center pt-4 mt-6 border-t border-tinta/10">
          <button
            onClick={onReset}
            className="flex items-center gap-2 bg-mostarda hover:bg-mostarda-light text-tinta border border-mostarda-dark/30 px-5 py-2.5 rounded-lg text-sm font-bold transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Realizar novo sorteio
          </button>
        </div>
      </div>
    );
  }

  // Draw execution screen
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl sm:text-2xl font-bold text-tinta leading-tight">
          Confirmar e <span className="italic text-bordo">sortear</span>
        </h2>
        <p className="mt-1 text-xs sm:text-sm text-tinta-soft italic">
          Revise as informações e execute o sorteio
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Products Summary */}
        <div className="bg-white border border-tinta/10 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Gift className="w-4 h-4 text-bordo" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-tinta">
              Produtos ({selectedProducts.length})
            </h3>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {selectedProducts.map((product) => (
              <div key={product.id} className="flex items-center gap-3 p-2 bg-creme-light border border-tinta/10 rounded-lg">
                {product.image_url && (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-12 h-12 object-cover rounded-md border border-tinta/10 flex-shrink-0"
                  />
                )}
                <span className="text-sm font-semibold text-tinta">{product.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Leads Summary */}
        <div className="bg-white border border-tinta/10 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <User className="w-4 h-4 text-bordo" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-tinta">
              Participantes ({selectedLeads.length})
            </h3>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {selectedLeads.map((lead) => (
              <div key={lead.id} className="p-2 bg-creme-light border border-tinta/10 rounded-lg">
                <p className="text-sm font-semibold text-tinta">{lead.customer_name}</p>
                <p className="text-xs text-tinta-soft">{formatPhone(lead.whatsapp)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-mostarda/10 border border-mostarda/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-mostarda-dark flex-shrink-0 mt-0.5" />
          <div className="text-sm text-tinta">
            <p className="text-xs font-bold uppercase tracking-wider text-mostarda-dark mb-2">Como funciona o sorteio</p>
            <ul className="list-disc list-inside space-y-1 text-tinta-soft">
              <li><span className="italic text-bordo font-semibold">Um</span> ganhador será selecionado aleatoriamente entre os participantes</li>
              <li>O ganhador receberá <span className="font-bold text-tinta">todos os {selectedProducts.length} produtos</span> selecionados</li>
              <li>O estoque dos produtos será decrementado automaticamente</li>
              <li>O ganhador receberá uma notificação no WhatsApp (se conectado)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-bordo/8 border border-bordo/30 rounded-lg p-4">
          <p className="text-sm text-bordo font-semibold">{error}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between gap-3 pt-4 mt-6 border-t border-tinta/10">
        <button
          onClick={onBack}
          disabled={executing}
          className="flex items-center gap-2 px-4 py-2 border border-tinta/15 rounded-lg text-sm font-semibold text-tinta-soft hover:bg-creme-light hover:text-tinta disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>
        <button
          onClick={executeDraw}
          disabled={executing}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {executing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-creme"></div>
              Sorteando...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Sortear
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DrawExecution;
